import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Chip,
  Skeleton,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Style as StyleIcon,

  Search as SearchIcon,
  ToggleOn,
  ToggleOff,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../../../api';
import { toast } from 'react-toastify';
import FreeSampleSwitch from '../../../components/FreeSampleSwitch';

const FlashCardList = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  // "Free samples only": cards that play on the public Flashcards page.
  const [freeSampleOnly, setFreeSampleOnly] = useState(false);
  const [subjectFilter, setSubjectFilter] = useState('');
  const [topicFilter, setTopicFilter] = useState('');
  const [subTopicFilter, setSubTopicFilter] = useState('');
  const [schoolYearFilter, setSchoolYearFilter] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [subtopics, setSubtopics] = useState([]);
  const [schoolYears, setSchoolYears] = useState([]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchTerm), 400);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    api.get('admin/flash-cards/subjects').then((r) => {
      if (r.data?.success && r.data?.data) setSubjects(r.data.data);
    }).catch(() => {});
    api.get('admin/flash-cards/school-years').then((r) => {
      if (r.data?.success && r.data?.data) setSchoolYears(r.data.data);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!subjectFilter) {
      setTopics([]);
      setTopicFilter('');
      setSubtopics([]);
      setSubTopicFilter('');
      return;
    }
    api.get('admin/flash-cards/topics', { params: { subject_id: subjectFilter } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setTopics(r.data.data);
        else setTopics([]);
      })
      .catch(() => setTopics([]));
    setTopicFilter('');
    setSubtopics([]);
    setSubTopicFilter('');
  }, [subjectFilter]);

  useEffect(() => {
    if (!topicFilter) {
      setSubtopics([]);
      setSubTopicFilter('');
      return;
    }
    api.get('admin/flash-cards/subtopics', { params: { topic_id: topicFilter } })
      .then((r) => {
        if (r.data?.success && r.data?.data) setSubtopics(r.data.data);
        else setSubtopics([]);
      })
      .catch(() => setSubtopics([]));
    setSubTopicFilter('');
  }, [topicFilter]);

  const fetchList = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, per_page: rowsPerPage };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter !== '') params.status = statusFilter;
      if (freeSampleOnly) params.free_sample = 1;
      if (subjectFilter) params.subject_id = subjectFilter;
      if (topicFilter) params.topic_id = topicFilter;
      if (subTopicFilter) params.sub_topic_id = subTopicFilter;
      if (schoolYearFilter) params.school_year_id = schoolYearFilter;
      const res = await api.get('admin/flash-cards', { params });
      if (res.data?.success && res.data?.data) {
        const pag = res.data.data;
        setItems(Array.isArray(pag.data) ? pag.data : []);
        setTotalRecords(pag.total ?? 0);
      } else {
        setItems([]);
        setTotalRecords(0);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load flash cards');
      setItems([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter, freeSampleOnly, subjectFilter, topicFilter, subTopicFilter, schoolYearFilter]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchList();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [fetchList]);

  const handleDeleteClick = (row) => {
    setSelectedItem(row);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;
    try {
      await api.delete(`admin/flash-cards/${selectedItem.id}`);
      toast.success('Flash card deleted');
      setDeleteDialogOpen(false);
      setSelectedItem(null);
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to delete');
    }
  };

  const handleStatusToggle = async (row) => {
    const newStatus = row.status === 2 ? 1 : 2;
    try {
      await api.patch(`admin/flash-cards/${row.id}/status`, { status: newStatus });
      toast.success('Status updated');
      fetchList();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusLabel = (status) => (status === 2 ? 'Active' : 'Inactive');
  const getStatusColor = (status) => (status === 2 ? 'success' : 'default');

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography component="h1" sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 700, color: '#111827', lineHeight: 1.3, mb: 2 }}>
          Flash Cards
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, ml: 1 }}>
          <Typography variant="body1" sx={{ color: '#5a6c7d' }}>
            View, edit, delete and assign flash cards to subject, topic and subtopic
          </Typography>
          {!loading && (
            <Chip label={`Total: ${totalRecords}`} size="small" sx={{ bgcolor: '#667eea', color: 'white' }} />
          )}
        </Box>
      </Box>

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon /></InputAdornment>
              ),
            }}
            size="small"
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Subject</InputLabel>
            <Select
              value={subjectFilter}
              onChange={(e) => { setSubjectFilter(e.target.value); setPage(0); }}
              label="Subject"
            >
              <MenuItem value="">All</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={!subjectFilter}>
            <InputLabel>Topic</InputLabel>
            <Select
              value={topicFilter}
              onChange={(e) => { setTopicFilter(e.target.value); setPage(0); }}
              label="Topic"
            >
              <MenuItem value="">All</MenuItem>
              {topics.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }} disabled={!topicFilter}>
            <InputLabel>Sub topic</InputLabel>
            <Select
              value={subTopicFilter}
              onChange={(e) => { setSubTopicFilter(e.target.value); setPage(0); }}
              label="Sub topic"
            >
              <MenuItem value="">All</MenuItem>
              {subtopics.map((st) => (
                <MenuItem key={st.id} value={st.id}>{st.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>School year</InputLabel>
            <Select
              value={schoolYearFilter}
              onChange={(e) => { setSchoolYearFilter(e.target.value); setPage(0); }}
              label="School year"
            >
              <MenuItem value="">All</MenuItem>
              {schoolYears.map((sy) => (
                <MenuItem key={sy.id} value={sy.id}>{sy.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              label="Status"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Inactive</MenuItem>
              <MenuItem value="2">Active</MenuItem>
            </Select>
          </FormControl>
          <Chip
            label="Free samples only"
            color={freeSampleOnly ? 'success' : 'default'}
            variant={freeSampleOnly ? 'filled' : 'outlined'}
            onClick={() => { setFreeSampleOnly((v) => !v); setPage(0); }}
            sx={{ fontWeight: 600 }}
          />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/admin/flash-cards/add')}
          sx={{ bgcolor: '#667eea', '&:hover': { bgcolor: '#5a6fd6' } }}
        >
          Add Flash Card
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 600 }}>Title</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Subject</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Sub topic</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>School year</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Free sample</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={60} /></TableCell>
                    <TableCell><Skeleton variant="text" width={70} /></TableCell>
                    <TableCell><Skeleton variant="text" width={50} /></TableCell>
                    <TableCell><Skeleton variant="text" width={50} /></TableCell>
                    <TableCell><Skeleton variant="text" width={80} /></TableCell>
                    <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                  </TableRow>
                ))
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                    <StyleIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                    <Typography color="text.secondary">No flash cards found</Typography>
                    <Button startIcon={<AddIcon />} onClick={() => navigate('/admin/flash-cards/add')} sx={{ mt: 2 }}>
                      Add Flash Card
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.subject_name || '—'}</TableCell>
                    <TableCell>{row.topic_name || '—'}</TableCell>
                    <TableCell>{row.sub_topic_name || '—'}</TableCell>
                    <TableCell>{row.school_year_name || '—'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(row.status)}
                        color={getStatusColor(row.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <FreeSampleSwitch
                        type="flash_card"
                        id={row.id}
                        value={row.is_free_sample}
                        inactive={row.status !== 2}
                        onChange={(next) => freeSampleOnly && !next && fetchList()}
                      />
                    </TableCell>
                    <TableCell>{row.created_at ? new Date(row.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={() => handleStatusToggle(row)}
                        title={row.status === 2 ? 'Set Inactive' : 'Set Active'}
                      >
                        {row.status === 2 ? <ToggleOn color="primary" /> : <ToggleOff />}
                      </IconButton>
                      <IconButton size="small" color="primary" onClick={() => navigate(`/admin/flash-cards/${row.id}/edit`)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" color="error" onClick={() => handleDeleteClick(row)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        {!loading && items.length > 0 && (
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        )}
      </Paper>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Flash Card</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{selectedItem?.title}&quot;? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FlashCardList;
